import { clsx } from 'clsx';
import React from 'react';
import type { User } from '../../../../../js/api/models/User';
import { formatDate } from '../../../utils/date';
import {
  neutralFinalCategoryKindStyle,
  positiveFinalCategoryKindStyle,
} from '../../../utils/statusStyles';

const CardHeading: React.FC<React.ComponentPropsWithoutRef<'p'>> = ({
  className,
  children,
  ...props
}) => {
  return (
    <p
      {...props}
      className={clsx(className, 'tw:!text-[16px] tw:font-semibold tw:text-zinc-900 tw:!mb-0')}
    >
      {children}
    </p>
  );
};

export const UserCard: React.FC<{
  user: User;
  roleLabelMap?: Record<string, string>;
  isSelected?: boolean;
  showOrganization?: boolean;
  actions?: React.ReactNode;
}> = ({ user, roleLabelMap = {}, isSelected = false, showOrganization = false, actions }) => {
  const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ') || '—';
  const status = user.is_active ? 'Active' : 'Inactive';
  const roleLabel = user.role ? roleLabelMap[user.role] || user.role : null;

  return (
    <div
      data-testid={`user-card-${user.id}`}
      className={clsx(
        'tw:h-full tw:bg-white tw:rounded-lg tw:shadow-sm tw:border tw:border-zinc-200 tw:hover:shadow-md tw:hover:bg-muted tw:transition-shadow',
        isSelected && 'tw:!bg-brand-green-0/10 tw:!shadow-md'
      )}
    >
      <div className="tw:p-3 tw:flex tw:flex-col tw:h-full tw:min-h-0">
        <div className="tw:flex-1 tw:min-h-0">
          <div className="tw:flex tw:justify-between tw:items-start tw:mb-2">
            <CardHeading className="tw:line-clamp-2">
              <i className="far fa-fw fa-lg fa-id-badge tw:mr-1" />
              {fullName}
            </CardHeading>
            <span
              className={clsx(
                'tw:rounded-full tw:border tw:text-center tw:text-xs tw:px-2 tw:py-1',
                status === 'Active' ? positiveFinalCategoryKindStyle : neutralFinalCategoryKindStyle
              )}
            >
              {status}
            </span>
          </div>
          {showOrganization && user.healthcare_entity_name && (
            <div
              data-testid={`user-card-org-${user.id}`}
              className="tw:text-xs tw:text-zinc-500 tw:mb-1"
            >
              {user.healthcare_entity_name}
            </div>
          )}
          {user.email && (
            <div className="tw:flex tw:items-center tw:gap-2 tw:mb-1 tw:text-sm tw:text-zinc-600">
              <i className="far fa-fw fa-envelope tw:text-zinc-400" />
              <span className="tw:truncate">{user.email}</span>
            </div>
          )}
          {roleLabel && (
            <div className="tw:flex tw:items-center tw:gap-2 tw:mb-1 tw:text-sm tw:text-zinc-600">
              <i className="far fa-fw fa-user-tag tw:text-zinc-400" />
              <span>{roleLabel}</span>
            </div>
          )}
          <div className="tw:mt-2 tw:text-xs tw:text-zinc-500 tw:space-y-0.5">
            <div>Last login: {formatDate(user.last_login)}</div>
            <div>Joined: {formatDate(user.rep_created_at)}</div>
          </div>
        </div>
        {actions && (
          <div
            className="tw:mt-auto tw:flex tw:gap-1"
            onClick={(e) => e.stopPropagation()}
            role="button"
            tabIndex={0}
            onKeyUp={(e) => {
              if (e.key === ' ' || e.key === 'Enter') {
                e.stopPropagation();
              }
            }}
          >
            {actions}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserCard;
